import { cn } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/**
 * Built-in strings of {@link Pagination}, overridable per locale (tech §59).
 *
 * @property navigation - Accessible name of the `<nav>`. Default `Pagination`.
 * @property previousPage - Accessible name of the previous control. Default `Previous page`.
 * @property nextPage - Accessible name of the next control. Default `Next page`.
 * @property previous - Visible text of the previous control. Default `Previous`.
 * @property next - Visible text of the next control. Default `Next`.
 * @property status - Page indicator; a function because word order differs by locale. Default `Page N of M`.
 */
export interface PaginationLabels {
	navigation: string
	previousPage: string
	nextPage: string
	previous: string
	next: string
	status: (page: number, pageCount: number) => string
}

const DEFAULT_LABELS: PaginationLabels = {
	navigation: 'Pagination',
	previousPage: 'Previous page',
	nextPage: 'Next page',
	previous: 'Previous',
	next: 'Next',
	status: (page, pageCount) => `Page ${page} of ${pageCount}`,
}

/**
 * Props for the {@link Pagination} molecule.
 *
 * @property page - The current 1-based page; the component never changes it.
 * @property pageCount - Total pages; `<= 1` renders nothing.
 * @property onPageChange - Reports the target page when an enabled control is activated.
 * @property className - Extra Tailwind classes merged onto the container.
 * @property labels - Localised built-in strings; each omitted key falls back to English.
 *
 * @example
 * ```tsx
 * <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
 * ```
 */
export interface PaginationProps {
	page: number
	pageCount: number
	onPageChange: (page: number) => void
	className?: string
	labels?: Partial<PaginationLabels>
}

const controlClasses =
	'rounded-ds-md border border-ds-border px-3 py-1.5 text-ds-sm text-ds-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50'

/**
 * Previous / next paging controls with a page indicator.
 *
 * @remarks
 * - **Bounded:** previous is disabled on the first page and next on the last, so
 *   no out-of-range page can ever be reported.
 * - **Hidden when trivial:** a `pageCount` of `1` or `0` renders nothing, so a
 *   consumer need not conditionally mount it.
 * - **Presentational only:** it never changes `page`; the target page is reported
 *   through `onPageChange` and the consumer's hook holds the value.
 * - **Styling:** every colour, radius and spacing class is a `ds-*` token utility.
 *
 * @see packages/ui-components/src/pagination/pagination.stories.tsx for visual examples
 */
export function Pagination({ page, pageCount, onPageChange, className, labels }: PaginationProps): ReactElement | null {
	if (pageCount <= 1) return null
	const text = { ...DEFAULT_LABELS, ...labels }

	return (
		<nav aria-label={text.navigation} className={cn('flex items-center justify-center gap-3', className)}>
			<button
				type="button"
				aria-label={text.previousPage}
				disabled={page === 1}
				onClick={() => onPageChange(page - 1)}
				className={controlClasses}
			>
				{text.previous}
			</button>
			<span data-testid="pagination-status" className="text-ds-sm text-ds-text-muted">
				{text.status(page, pageCount)}
			</span>
			<button
				type="button"
				aria-label={text.nextPage}
				disabled={page === pageCount}
				onClick={() => onPageChange(page + 1)}
				className={controlClasses}
			>
				{text.next}
			</button>
		</nav>
	)
}
